<template>
  <ChatListSkeleton v-if="loadingChats" />
  <div
    v-else
    class="chat-list-wrapper d-flex flex-column fill-height"
  >
    <!-- Fixed Header -->
    <div class="chat-header text-center py-4 px-4">
      <h1 class="text-title font-weight-medium">
        Chats
      </h1>
    </div>

    <!-- Scrollable Chat List -->
    <div class="chat-list-scroll flex-grow-1 overflow-y-auto px-2">
      <v-list density="compact">
        <template
          v-for="(chatGroup, label) in sortedGroupedChats"
          :key="label"
        >
          <v-list-subheader
            class="text-subtitle-2 font-weight-bold text-grey-darken-1"
          >
            {{ label }}
          </v-list-subheader>
          <ChatItem
            v-for="chat in chatGroup"
            :key="chat.id"
            :chat="chat"
            :is-active="chat.id === currentChat?.id"
            @select="handleSelect"
          />
          <v-divider class="my-3" />
        </template>
      </v-list>
    </div>

    <!-- Fixed Bottom Button -->
    <div class="chat-footer px-4 py-4">
      <v-divider class="mb-3" />
      <v-btn
        color="primary"
        variant="outlined"
        block
        @click="startNewChat"
      >
        + New Chat
      </v-btn>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed } from 'vue';
  import ChatItem from '@/interfaces/components/ChatItem.vue';
  import { groupChatByLabel } from '@/interfaces/utils/groupChats';
  import type { Chat } from '@/types/chat';
  import { authState } from '@/interfaces/composables/auth/authState';

  import {
    chats,
    startNewChat,
    selectChat,
    currentChat,
  } from '@/interfaces/composables/useChatManager';
  import ChatListSkeleton from '@/interfaces/components/skeletons/ChatListSkeleton.vue';

  const groupedChats = computed(() => groupChatByLabel(chats.value));

  const loadingChats = computed(() => {
    return !authState.isAuthenticated.value;
  });

  function handleSelect(chatId: string) {
    selectChat(chatId);
  }

  const sortedGroupedChats = computed(() => {
    const groups = groupedChats.value;
    const customOrder = ['Today', 'Yesterday', 'Last 7 Days', 'Last 30 Days'];

    const result: Record<string, Chat[]> = {};

    for (const key of customOrder) {
      if (groups[key]) {
        result[key] = [...groups[key]].sort(
          (a, b) => b.last_message_at.getTime() - a.last_message_at.getTime()
        );
      }
    }

    const months = Object.keys(groups)
      .filter((k) => !customOrder.includes(k))
      .sort((a, b) => {
        const aTime = groups[a][0].last_message_at.getTime();
        const bTime = groups[b][0].last_message_at.getTime();
        return bTime - aTime;
      });

    for (const key of months) {
      result[key] = [...groups[key]].sort(
        (a, b) => b.last_message_at.getTime() - a.last_message_at.getTime()
      );
    }

    return result;
  });
</script>

<style scoped lang="sass">
  .chat-list-wrapper
    height: 100%
    display: flex
    flex-direction: column

  .chat-header
    flex-shrink: 0

  .chat-list-scroll
    overflow-y: scroll // not auto
    scrollbar-gutter: stable // modern browsers only
    min-height: 0
    flex-grow: 1


  .chat-footer
    flex-shrink: 0
    background-color: white
    box-shadow: 0 -2px 4px rgba(0, 0, 0, 0.04)
    z-index: 1
</style>
